import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../features/auth/context';
import { useToast } from '../hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { getAdminWaitlistEntries, AdminWaitlistEntry } from '../features/admin/api';
import Table from '../components/ui/table';
import type { Column } from '../components/ui/table';
import Button from '../components/ui/button';
import AdminCsvExportButton from '../features/admin/AdminCsvExportButton';

// Admin subpage: the pre-launch waitlist, kept as a read-only archive after
// the waitlist sunset (Aug 2026). The table used to live inline on the main
// Admin Panel; it moved here so the panel stays focused on registered users.
// The entries still matter operationally: they are part of the email campaign
// audience (backend/services/campaigns.js).
// Backend: GET /api/admin/waitlist. Data source: waitlist_entries table.

// Format a date as Today / Yesterday / "Mon D, YYYY" (same helper style as
// the Admin Panel's user table).
function formatDate(dateString: string) {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

  if (dateOnly.getTime() === todayOnly.getTime()) return 'Today';
  if (dateOnly.getTime() === yesterdayOnly.getTime()) return 'Yesterday';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

const columns: Column<AdminWaitlistEntry>[] = [
  {
    key: 'firstName',
    header: 'First Name',
    className: 'min-w-[10ch]',
    render: (r) => r.firstName || '-'
  },
  {
    key: 'email',
    header: 'Email Address',
    className: 'min-w-[16ch]',
    render: (r) => <span className="text-sm font-mono">{r.email}</span>
  },
  {
    key: 'createdAt',
    header: 'Joined',
    className: 'min-w-[10ch]',
    render: (r) => formatDate(r.createdAt)
  },
  {
    key: 'acceptedTermsAt',
    header: 'Consent Time',
    className: 'min-w-[10ch]',
    render: (r) => formatDate(r.acceptedTermsAt)
  },
  {
    key: 'source',
    header: 'Source',
    className: 'min-w-[10ch]',
    render: (r) => r.source || 'unknown'
  },
  {
    key: 'location',
    header: 'Location',
    className: 'min-w-[14ch]',
    render: (r) => {
      const parts = [r.city, r.country].filter(Boolean);
      return parts.length ? parts.join(', ') : <span className="text-slate-500 text-xs">-</span>;
    }
  },
  {
    key: 'interests',
    header: 'Interests',
    className: 'min-w-[20ch]',
    render: (r) => {
      if (!r.interests || r.interests.length === 0) {
        return <span className="text-slate-500 text-xs">none</span>;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {r.interests.map((interest) => (
            <span
              key={interest}
              className="px-2 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200"
            >
              {interest.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      );
    }
  }
];

const AdminWaitlistPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [rows, setRows] = useState<AdminWaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!user || !user.isAdmin)) {
      showToast({ title: 'Access Denied', message: 'Admin access required. Redirecting to homepage.', type: 'error' });
      navigate('/');
    }
  }, [authLoading, user, navigate, showToast]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminWaitlistEntries();
      setRows(data);
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Failed to load waitlist entries.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  if (authLoading || !user || !user.isAdmin) return null;

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Waitlist Archive</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={() => navigate('/admin')}>← Admin</Button>
          <Button variant="secondary" onClick={load} disabled={loading}>Refresh</Button>
        </div>
      </div>

      <p className="text-sm text-slate-600 mb-4">
        Pre-launch signups, read-only since the waitlist sunset (Aug 2026). Still part of the
        email campaign audience.
      </p>

      {loading ? (
        <div className="text-slate-600">Loading waitlist entries…</div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-sm text-slate-600">{rows.length} entr{rows.length === 1 ? 'y' : 'ies'}, newest first</div>
            <AdminCsvExportButton table="waitlist" label="waitlist entries" />
          </div>
          <div className="table-compact">
            <Table columns={columns} data={rows} emptyMessage="No waitlist entries found." />
          </div>
        </>
      )}
    </div>
  );
};

export default AdminWaitlistPage;
